import type { AnyAction } from 'redux';
import type {
  Customer,
  LaborRate,
  LaborRateResponse,
  MobileCheckinReportResponse,
  MpiTemplate,
  MpiTemplates,
  NonRoPayment,
  OpCodesSettingsResponse,
  OperationCode,
  PriceMatrix,
  RepairOrderInteractionsResponse,
  UnreadMessageCounts,
  User,
  VideoTroubleshootingReportResponse,
} from 'types';
import type { MessageType } from 'redux/actions/member/messages';

import { MemberActionTypes, ROS_STATE_PROPS } from 'redux/actions/member';
import { toBool } from 'utility/helpers/format';
import { UserRoles as UserRolesEnum } from 'types';

import { QuotingActionTypes } from '../quoting/action-types';

export type MemberInitialState = {
  currentPage: number;
  sortField: string;
  sortDirection: string;
  searchTerm: string;
  repairOrderInteractions: null | RepairOrderInteractionsResponse;
  currentMessagePage: number;
  customers: Customer | any; // TODO: This 'customers' prop appears unused. Investigate its necessity. Consider moving 'getCustomers' action to a custom hook.
  error: null | Error;
  filters: any[];
  isChatModalOpen: boolean;
  isCrudModalOpen: boolean;
  isEditClicked: boolean;
  isFetchingROs: boolean;
  isLoading: boolean;
  isLockedOut: boolean;
  isLockedOutLoading: boolean;
  isMpiGeneratorOpen: boolean;
  staleRepairOrderIds: number[];
  fetchingLaborRates: boolean;
  laborRates: null | LaborRate[];
  laborRatesResponse: LaborRateResponse | null;
  messageType: MessageType | null;
  openModals: string[];
  priceMatrix: PriceMatrix[];
  printableDoc: null | React.JSX.Element;
  // Saved service texts (Quick SMS), set by getServiceQuickSMS.
  quickSMS: null | ReadonlyArray<{ id: number; message: string }>;
  paymentFormLoaded: boolean;
  role: null | UserRolesEnum;
  selectedMpiTemplate: null | MpiTemplate;
  selectedRecipient: null | Customer;
  selectedRow: null | any; // TODO type as RepairOrder | null
  unreadMessageCounts: any | UnreadMessageCounts;
  users: User[];
  isPhoneValid: null | boolean;
  customerROs: any[]; //TODO: just a list of repair orders?
  chatInputValue: string;
  isApproved: boolean | null;

  // Vehicle Lists (used by various features)
  vehicleMakes: any[];
  vehicleModels: any[];
  vehicleYears: any[];
  reportingCustomer: any | Record<string, never>;

  // Reports
  advisorReport: any | Record<string, never>;
  advisorUsageReport: any | Record<string, never>;
  advisorUsageReportForRVIndustry: any | Record<string, never>;
  customerEngagementReport: any | Record<string, never>;
  customerEngagementReportForRVIndustry: any | Record<string, never>;
  declinedServiceReport: any | Record<string, never>;
  financeReporting: any | Record<string, never>;
  followUpReporting: any | Record<string, never>;
  iPayReport: any | Record<string, never>;
  payments: NonRoPayment[] | null;
  mpiReport: any | Record<string, never>;
  partsReporting: any | Record<string, never>;
  roReporting: any | Record<string, never>;
  technicianReport: any | Record<string, never>;
  technicianUsageReport: any | Record<string, never>;
  recommendationReport: any | Record<string, never>;
  advisorRecommendationReport: any | Record<string, never>;
  technicianRecommendationReport: any | Record<string, never>;
  mobileCheckinReport: MobileCheckinReportResponse | Record<string, never>;
  videoTroubleshootingReport: VideoTroubleshootingReportResponse | Record<string, never>;

  // Users
  [UserRolesEnum.SERVICE_ADVISOR]: any | Record<string, never>;
  [UserRolesEnum.SERVICE_MANAGER]: any | Record<string, never>;
  [UserRolesEnum.PARTS_ADVISOR]: any | Record<string, never>;
  [UserRolesEnum.SALES_AGENT]: any | Record<string, never>;
  [UserRolesEnum.SALES_MANAGER]: any | Record<string, never>;
  [UserRolesEnum.TECHNICIAN]: any | Record<string, never>;

  // Settings
  settings: null | any;
  environmentSettings: null | any;
  opCodes: any | Record<string, never>;
  mpiTemplates: MpiTemplates;
  opCodesResponse: OpCodesSettingsResponse | null;
  coupons: any | Record<string, never>;
  parts: any[];
  rewardsTier: any | Record<string, never>;
  transportationType: any | Record<string, never>;
  serviceOffHour: any | Record<string, never>;
  factoryMaintenanceOpCode: OperationCode | null;

  // Follow Up
  followUpTargets: any[];

  // myReview
  myReviewAdvisorReport: any[];
  roReport: any[];

  // Dashboards
  advisorDashboard: any | Record<string, never>;
  financeDashboard: any | Record<string, never>;
  images: any[];
  mpiTemplate: MpiTemplate | null;
  ros: any | Record<string, never>;
  upgrade: any | Record<string, never>;
  videos: any[];

  // Messaging
  messages: any | Record<string, never>;

  // Customer Messaging
  customerMessages: any | Record<string, never>;

  // CheckIn
  checkIn: any | Record<string, never>;

  // Service Phases
  servicePhases: any[];

  // Service Express
  serviceExpress: any[];

  //Parts Express
  isPeModalClosed: boolean;

  selectedCustomer: null | any;
  reconditioningExpress: any[];

  // Platform
  partsSources: any[];
  partsMatrices: any[];

  reconditioningPhases: any[];

  /**
   * Page title text for the current page
   * Used to display a custom title in the navbar
   */
  pageTitleText: string;
};

const initialState: MemberInitialState = {
  currentPage: 1,
  sortField: '',
  sortDirection: '',
  searchTerm: '',
  repairOrderInteractions: null,
  currentMessagePage: 1,
  customers: {},
  error: null, // error object
  filters: [],
  isChatModalOpen: false,
  isCrudModalOpen: false, // so successful CRUD operations can toggle modals
  isEditClicked: false,
  isFetchingROs: false,
  isLoading: false, // used to show loader and prevent user interaction during requests
  isLockedOut: false,
  isLockedOutLoading: false,
  isMpiGeneratorOpen: false,
  staleRepairOrderIds: [], // id of stale repair order, triggers dashboard refresh
  fetchingLaborRates: false,
  laborRates: null,
  laborRatesResponse: null,
  messageType: null,
  openModals: [],
  priceMatrix: [],
  printableDoc: null,
  quickSMS: null,
  paymentFormLoaded: false,
  role: null,
  selectedMpiTemplate: null,
  selectedRecipient: null,
  selectedRow: null,
  unreadMessageCounts: {},
  users: [],
  isPhoneValid: null,
  customerROs: [],
  chatInputValue: '',
  isApproved: null,

  // Vehicle Lists
  vehicleMakes: [],
  vehicleModels: [],
  vehicleYears: [],
  reportingCustomer: {},

  // Reports
  advisorReport: {},
  advisorUsageReport: {},
  advisorUsageReportForRVIndustry: {},
  customerEngagementReport: {},
  customerEngagementReportForRVIndustry: {},
  declinedServiceReport: {},
  financeReporting: {},
  followUpReporting: {},
  iPayReport: {},
  payments: null,
  mpiReport: {},
  partsReporting: {},
  roReporting: {},
  technicianReport: {},
  technicianUsageReport: {},
  recommendationReport: {},
  advisorRecommendationReport: {},
  technicianRecommendationReport: {},
  mobileCheckinReport: {},
  videoTroubleshootingReport: {},

  // Users
  [UserRolesEnum.SERVICE_ADVISOR]: {},
  [UserRolesEnum.SERVICE_MANAGER]: {},
  [UserRolesEnum.PARTS_ADVISOR]: {},
  [UserRolesEnum.SALES_AGENT]: {},
  [UserRolesEnum.SALES_MANAGER]: {},
  [UserRolesEnum.TECHNICIAN]: {},

  // Settings
  settings: null,
  environmentSettings: null,
  opCodes: {},
  mpiTemplates: [],
  opCodesResponse: null,
  coupons: {},
  parts: [],
  rewardsTier: {},
  transportationType: {},
  serviceOffHour: {},
  factoryMaintenanceOpCode: null,

  // Follow Up
  followUpTargets: [],

  // myReview
  myReviewAdvisorReport: [],
  roReport: [],

  // Dashboards
  advisorDashboard: {},
  financeDashboard: {},
  images: [],
  mpiTemplate: null,
  ros: {},
  upgrade: {},
  videos: [],

  // Messaging
  messages: {},

  // Customer Messaging
  customerMessages: {},

  // CheckIn
  checkIn: {},

  // Service Phases
  servicePhases: [],

  // Service Express
  serviceExpress: [],

  //Parts Express
  isPeModalClosed: false,

  selectedCustomer: null,
  reconditioningExpress: [],

  // Platform
  partsSources: [],
  partsMatrices: [],

  reconditioningPhases: [],

  pageTitleText: '',
};

const BOOLEAN_SETTINGS = [
  'activateIntegrationSms',
  'additionalQuoteCompliance',
  'applyProcessingFeeToCredit',
  'applyProcessingFeeToDebit',
  'applyProcessingFeeToManual',
  'applyProcessingFeeToTerminal',
  'applyProcessingFeeToText',
  'checkin',
  'checkout',
  'customerPhotos',
  'customLaborPriceWriteBack',
  'dealershipPackages',
  'deferrals',
  'demo',
  'dmsAppointmentWriteBack',
  'dmsQuoteWriteBack',
  'dmsRepairOrderWriteback',
  'dmsVehicleOwnerTransfer',
  'dmsVehicleOwnershipSync',
  'emailNotifications',
  // ISA-2074 Epic 1 Story 1.3 (AD-2): first FE consumer of this flag's actual boolean value
  // (gating the check-in Dispatch-priority control render). Without this, a stored '0' would
  // read as truthy JS ('0' is a non-empty string), silently breaking "flag off -> inert" (AC4).
  'enableIntelligentDispatch',
  'enableVoidPaymentFeature',
  'estimatePrintLayout',
  'factoryMaintenanceMenu',
  'globalPay',
  'heartland',
  'includePreApprovedInPicklist',
  'internalInspectionSignoff',
  'isSandboxEnforced',
  'iServiceActivated',
  'mpi',
  'mpiSendToCustomer',
  'msms',
  'myReviewActivated',
  'nmi',
  'partLaborGuide',
  'parts',
  'partsExpressPreventPreApprovalTaskCreation',
  'pricingUsePartsMatrix',
  'quote',
  'quoteSuppliesLabor',
  'quoteSuppliesParts',
  'quoteSuppliesTaxable',
  'reconditioningPhases', // TODO ??????
  'repairOrderTeams',
  'rewards',
  'scheduler',
  'schedulerBodyLane',
  'schedulerExpressLane',
  'schedulerMainLane',
  'serviceExpress',
  'servicePhases',
  'showCashPrice',
  'showCustomerNameOnTechnicianDashboard',
  'signedDocumentCodeVerification',
  'smsNotifications',
  'technicianWriteBack',
  'usingAutomate',
  'usingAutoSoft',
  'usingCdkFortellis',
  'usingCdk',
  'usingDealerBuilt',
  'usingDealerTrack',
  'usingFordIntegration',
  'usingGoodyear',
  'usingReynolds',
  'uveye',
  'video',
  'vinScanImageResize',
];

const receiveSettings = (state: MemberInitialState, action: AnyAction, callback: any) => {
  const settings = action.data.reduce((acc: any, setting: any) => {
    acc[setting.key] = setting.value;
    return acc;
  }, {});

  settings.hasPayments =
    settings.nmi === '1' || settings.heartland === '1' || settings.globalPay === '1' ? '1' : '0';

  const additionalParams = callback(settings);

  return { ...state, settings, ...additionalParams };
};

const memberReducer = (state = initialState, action: AnyAction) => {
  const { data, stateProp } = action;

  switch (action.type) {
    case MemberActionTypes.SET_STATE:
      return { ...state, [action.key]: action.value };
    case MemberActionTypes.SET_FILTERS:
      return { ...state, filters: data };
    case MemberActionTypes.CLOSE_REPAIR_ORDER:
      return { ...state, isLoading: true };
    case MemberActionTypes.RO_OUTDATED: {
      const staleRepairOrderIds = [...state.staleRepairOrderIds];
      if (state.selectedRow?.id) {
        staleRepairOrderIds.push(state.selectedRow.id);
      }
      if (action.forceUpdate) {
        // Hack to force GET ROs on creating new record (ID not available yet)
        staleRepairOrderIds.push(-1);
      }

      return {
        ...state,
        staleRepairOrderIds,
      };
    }
    case MemberActionTypes.RECEIVE_REPAIR_ORDER_INTERACTIONS:
      return { ...state, repairOrderInteractions: data };
    case MemberActionTypes.DISMISS_ERROR:
      return { ...state, error: null };
    case MemberActionTypes.GET_MPI_TEMPLATES:
      return {
        ...state,
        isLoading: true,
        mpiTemplate: null,
        mpiTemplates: [],
      };
    case MemberActionTypes.GET_MPI_TEMPLATE_BY_ID:
      return { ...state, mpiTemplate: null, isLoading: true };
    case MemberActionTypes.GET_REPAIR_ORDER_IMAGES:
      return { ...state, images: [], isLoading: true };
    case MemberActionTypes.REPAIR_ORDER_IMAGE_UPDATED:
      return { ...state, isLoading: false };
    case MemberActionTypes.MPI_RESULTS_SUBMITTED:
      return {
        ...state,
        isLoading: false,
        mpiTemplate: null,
        mpiTemplates: [],
      };
    case MemberActionTypes.RECEIVE_MPI_TEMPLATES:
      return { ...state, mpiTemplates: data, isLoading: false };
    case MemberActionTypes.RECEIVE_MPI_TEMPLATE_BY_ID:
      return { ...state, mpiTemplate: data, isLoading: false };
    case MemberActionTypes.RECEIVE_OP_CODES:
      return { ...state, opCodes: data, isLoading: false };
    case MemberActionTypes.SET_OP_CODES_RESPONSE: {
      return { ...state, opCodesResponse: data };
    }
    case MemberActionTypes.RECEIVE_REPAIR_ORDER_IMAGES:
      return { ...state, images: data, isLoading: false };
    case MemberActionTypes.RECEIVE_VEHICLE_LISTS: {
      const vehicleMakes = data[0].data;
      const vehicleModels = data[1].data;
      const vehicleYears = data[2].data;

      return { ...state, vehicleMakes, vehicleModels, vehicleYears };
    }
    case MemberActionTypes.RESEND_WAIVER:
      return { ...state, isLoading: true };
    case MemberActionTypes.REPAIR_ORDER_CLOSED:
      return { ...state, isLoading: false };
    case MemberActionTypes.RESET_MEMBER_STATE:
      return initialState;
    case MemberActionTypes.SET_CURRENT_PAGE:
      return { ...state, currentPage: action.page };
    case MemberActionTypes.SET_BACKGROUND_COLOR:
      return { ...state, backgroundColor: action.color };
    case MemberActionTypes.SET_SCHEDULED_MAINTENANCE:
      return { ...state, scheduledMaintenance: data };
    case MemberActionTypes.SET_APPROVED_ONLY:
      return { ...state, isApproved: data };
    case MemberActionTypes.SET_SELECTED_ROW:
      return {
        ...state,
        selectedRecipient: data?.primaryCustomer,
        selectedRow: data,
        repairOrderInteractions: null,
        images: [],
      };
    case MemberActionTypes.CLEAR_SELECTED_REPAIR_ORDER:
      return { ...state, selectedRow: null };
    case MemberActionTypes.SUBMIT_MPI_RESULTS:
      return { ...state, isLoading: true };
    case MemberActionTypes.TOGGLE_MPI_GENERATOR:
      return { ...state, isMpiGeneratorOpen: !state.isMpiGeneratorOpen };
    case MemberActionTypes.UPDATE_SELECTED_MPI_TEMPLATE:
      return { ...state, selectedMpiTemplate: data };
    case MemberActionTypes.UPDATE_TABLE_PARAMETER:
      return { ...state, ...action.params };
    case MemberActionTypes.WAIVER_SENT:
      return { ...state, isLoading: false };
    case MemberActionTypes.PAYMENT_FORM_ERRORED:
      return { ...state, paymentFormLoaded: false };
    case MemberActionTypes.PAYMENT_FORM_LOADED:
      return { ...state, paymentFormLoaded: true };
    case MemberActionTypes.CLEAR_PAYMENTS:
      return { ...state, payments: null };
    case MemberActionTypes.RECEIVE_PAYMENTS:
      return { ...state, payments: data };
    case MemberActionTypes.RECEIVE_PAYMENT:
      return { ...state, payment: data };
    case QuotingActionTypes.FETCH_LABOR_RATES:
      return { ...state, fetchingLaborRates: data };
    case QuotingActionTypes.RECEIVE_LABOR_RATES:
      return { ...state, laborRates: data };
    case QuotingActionTypes.SET_LABOR_RATES_RESPONSE:
      return { ...state, laborRatesResponse: data };
    case MemberActionTypes.RECEIVE_UVEYE:
      return { ...state, uvEye: data, isLoading: false };
    case MemberActionTypes.SET_OPEN_DASHBOARD_MODAL:
      return { ...state, openModals: data };
    case 'SET_CUSTOMER_DEMO':
      return { ...state, customerDemo: data };
    case MemberActionTypes.SET_SELECTED_RECIPIENT:
      return { ...state, selectedRecipient: data };
    // Print
    case MemberActionTypes.SET_PRINTABLE_DOC_DATA:
      return { ...state, printableDoc: data };
    //  Mobile
    case MemberActionTypes.VALIDATING_MOBILE_PHONE:
      return { ...state, isPhoneValid: null };
    case MemberActionTypes.MOBILE_PHONE_VALIDATED:
      return { ...state, isPhoneValid: data };
    case MemberActionTypes.TOGGLE_MOBILE_VALIDATION_CONFIRM_MODAL:
      return { ...state, isPhoneValid: null };
    // SMS
    case MemberActionTypes.SET_QUICK_SMS:
      return { ...state, quickSMS: data };
    case MemberActionTypes.SET_CHAT_INPUT_VALUE:
      return { ...state, chatInputValue: data };
    case MemberActionTypes.TOGGLE_CHAT_MODAL:
      return {
        ...state,
        isChatModalOpen: !state.isChatModalOpen,
        selectedRecipient: null,
        // A conversation opened next (e.g. from a dashboard's text icon) must not start from these.
        messages: null,
      };
    case MemberActionTypes.SEND_MESSAGE:
      return {
        ...state,
        sendMessage: true,
      };
    // Loading
    case MemberActionTypes.LOADING_START:
      return { ...state, isLoading: true };
    case MemberActionTypes.LOADING_END:
      return { ...state, isLoading: false };
    // Service Express
    case MemberActionTypes.SET_SERVICE_EXPRESS:
      return { ...state, serviceExpress: data };
    case MemberActionTypes.SET_RO_SERVICE_PHASES:
      return { ...state, roServicePhases: data };
    case MemberActionTypes.SET_SERVICE_PHASES:
      return { ...state, servicePhases: data };
    case MemberActionTypes.DELETE_SERVICE_PHASE:
      return { ...state, servicePhases: state.servicePhases.filter((sp) => sp.id !== data.id) };
    // Reconditioning
    case MemberActionTypes.SET_RECONDITIONING_PHASES:
      return { ...state, reconditioningPhases: data };
    case MemberActionTypes.SET_RO_RECONDITIONING_PHASES:
      return { ...state, roReconditioningPhases: data };
    case MemberActionTypes.SET_RECONDITIONING_EXPRESS:
      return { ...state, reconditioningExpress: data };
    case MemberActionTypes.DELETE_RECONDITIONING_PHASE:
      return state;
    // TODO BUG
    // return {
    //   ...state,
    //   reconditioningPhases: state.reconditioningPhases.filter((sp) => sp.id !== data.id),
    // };
    // Parts
    case MemberActionTypes.CREATE_PARTS_MATRIX_ROW: {
      const updated = state.partsMatrices.map((m) => {
        if (data.partMatrixId === m.id) {
          return { ...m, partMatrixValues: [...m.partMatrixValues, data.row] };
        }
        return m;
      });
      return { ...state, partsMatrices: updated, isLoading: false };
    }
    case MemberActionTypes.UPDATE_PARTS_MATRIX_ROW: {
      const updated = state.partsMatrices.map((m) => {
        if (data.partMatrixId === m.id) {
          const partMatrixValues = m.partMatrixValues.map((r: any) =>
            r.id === data.row.id ? data.row : r,
          );
          return { ...m, partMatrixValues };
        }
        return m;
      });
      return { ...state, partsMatrices: updated, isLoading: false };
    }
    case MemberActionTypes.UPDATE_PARTS_MATRIX: {
      const updated = state.partsMatrices.map((m) => (m.id === data.id ? data : m));
      return { ...state, partsMatrices: updated, isLoading: false };
    }
    case MemberActionTypes.RECEIVE_PARTS:
      return { ...state, parts: data, isLoading: false };
    case MemberActionTypes.RECEIVE_PARTS_SOURCES:
      return { ...state, partsSources: data, isLoading: false };
    case MemberActionTypes.RECEIVE_PARTS_MATRICES:
      return { ...state, partsMatrices: data, isLoading: false };
    case MemberActionTypes.ADD_NEW_PARTS_SOURCE:
      return { ...state, partsSources: [...state.partsSources, data], isLoading: false };
    case MemberActionTypes.ADD_NEW_PARTS_MATRIX:
      return { ...state, partsMatrices: [...state.partsMatrices, data], isLoading: false };
    case MemberActionTypes.DELETE_PARTS_SOURCE: {
      const updated = state.partsSources.filter((m) => m.id !== data.id);
      return { ...state, partsSources: updated, isLoading: false };
    }
    case MemberActionTypes.DELETE_PARTS_MATRIX: {
      const updated = state.partsMatrices.filter((m) => m.id !== data.id);
      return { ...state, partsMatrices: updated, isLoading: false };
    }
    case MemberActionTypes.DELETE_PARTS_MATRIX_ROW: {
      const updated = state.partsMatrices.map((m) => {
        if (m.id === data.partMatrixId) {
          const partMatrixValues = m.partMatrixValues.filter((r: any) => r.id !== data.rowId);
          return { ...m, partMatrixValues };
        }
        return m;
      });
      return { ...state, partsMatrices: updated, isLoading: false };
    }
    case MemberActionTypes.CLOSE_PEMODAL:
      return {
        ...state,
        isPeModalClosed: data.isPesafModalClosed,
      };
    // Settings
    case MemberActionTypes.RECEIVE_SETTINGS:
      return receiveSettings(state, action, (settings: any) => {
        const converted = {} as any;
        BOOLEAN_SETTINGS.forEach((key) => {
          converted[key] = toBool(settings[key]);
        });

        converted.hasPayments = converted.nmi || converted.heartland || converted.globalPay;

        return {
          isLoading: false,
          settings: {
            ...settings,
            ...converted,
          },
        };
      });
    case MemberActionTypes.GET_SETTINGS:
      return { ...state, isLoading: true };
    case MemberActionTypes.UPDATE_SETTINGS:
      return { ...state, isLoading: true };
    case MemberActionTypes.RECEIVE_ENVIRONMENT_SETTINGS:
      return { ...state, environmentSettings: data };
    case MemberActionTypes.SETTING_LOCKOUT:
      return { ...state, isLockedOutLoading: data };
    // Price
    case MemberActionTypes.GET_PRICE_MATRIX:
      return { ...state, priceMatrix: [] };
    case MemberActionTypes.RECEIVE_PRICE_MATRIX:
      return { ...state, priceMatrix: data };
    // Customer
    case MemberActionTypes.GET_ROS_BY_CUSTOMER_PHONE:
      return { ...state, customerROs: [] };
    case MemberActionTypes.RECEIVE_ROS_BY_CUSTOMER_PHONE:
      return { ...state, customerROs: data, isLoading: false };
    case MemberActionTypes.SET_SELECTED_CUSTOMER:
      return { ...state, selectedCustomer: data };
    case MemberActionTypes.CUSTOMER_CREATED:
      return { ...state, selectedRecipient: data, isPhoneValid: null };
    // Record
    case MemberActionTypes.RECORD_CREATED:
      return { ...state, isCrudModalOpen: false, isLoading: false };
    case MemberActionTypes.RECORD_DELETED:
      return { ...state, isCrudModalOpen: false, isLoading: false };
    case MemberActionTypes.RECORD_UPDATED:
      return { ...state, isCrudModalOpen: false, isLoading: false };
    case MemberActionTypes.CREATE_RECORD:
      return { ...state, isLoading: true };
    case MemberActionTypes.DELETE_RECORD:
      return { ...state, isLoading: true };
    case MemberActionTypes.UPDATE_RECORD:
      return { ...state, isLoading: true };
    case MemberActionTypes.GET_RECORDS:
      return { ...state, isLoading: true, [stateProp]: {} };
    case MemberActionTypes.EMPTY_ROS:
      return { ...state, ros: {} };
    case MemberActionTypes.RECEIVE_RECORD:
      return {
        ...state,
        isLoading: false,
      };
    case MemberActionTypes.RECEIVE_RECORDS:
      return {
        ...state,
        isLoading: false,
        [stateProp]: data,
        staleRepairOrderIds:
          stateProp === 'ros' || stateProp === 'advisorDashboard' ? [] : state.staleRepairOrderIds,
      };
    // CRUD
    case MemberActionTypes.CRUD_EDIT_OPEN:
      return { ...state, isEditClicked: true };
    case MemberActionTypes.CRUD_EDIT_CLOSE:
      return { ...state, isEditClicked: false };
    case MemberActionTypes.TOGGLE_CRUD_MODAL: {
      const crudModal = !state.isCrudModalOpen ? stateProp : undefined;
      return { ...state, isCrudModalOpen: !state.isCrudModalOpen, crudModal };
    }
    // ROS
    case MemberActionTypes.GET_ROS:
      return { ...state, isFetchingROs: true };
    case MemberActionTypes.GET_ROS_BY_ADIVISOR_ID:
      return { ...state, advisorROs: [] };
    case MemberActionTypes.RECEIVE_ROS_BY_ADIVISOR_ID:
      return { ...state, advisorROs: data, isLoading: false };
    case MemberActionTypes.RECEIVE_ROS:
      return {
        ...state,
        [stateProp || ROS_STATE_PROPS.ROS]: data,
        isFetchingROs: false,
        staleRepairOrderIds:
          !stateProp ||
          stateProp === ROS_STATE_PROPS.ROS ||
          stateProp === ROS_STATE_PROPS.ADVISOR_DASHBOARD
            ? []
            : state.staleRepairOrderIds,
      };
    case MemberActionTypes.POLL_ROS_GET:
      return { ...state, isFetchingROs: true };
    case MemberActionTypes.POLL_ROS_CANCEL:
      return { ...state, isFetchingROs: false };
    case MemberActionTypes.POLL_ROS_RECEIVE:
      return {
        ...state,
        ros: data,
        isFetchingROs: false,
        staleRepairOrderIds: [],
      };
    // Handle Error
    case MemberActionTypes.HANDLE_ERROR:
      return { ...state, isLoading: false, error: action.error };
    // Videos
    case MemberActionTypes.GET_VIDEOS_BY_RO_ID:
      return { ...state, videos: [], isLoading: true };
    case MemberActionTypes.RECEIVE_VIDEOS_BY_RO_ID:
      return { ...state, videos: data, isLoading: false };
    // Message
    case MemberActionTypes.RESET_MESSAGES:
      return { ...state, messages: null };
    case MemberActionTypes.RECEIVE_MESSAGES:
      return { ...state, messages: data };
    case MemberActionTypes.RECEIVE_CUSTOMER_MESSAGES:
      return { ...state, customerMessages: data };
    case MemberActionTypes.SET_MESSAGE_TYPE:
      return { ...state, messageType: data };
    case MemberActionTypes.UPDATE_UNREAD_MESSAGE_COUNTS:
      return { ...state, unreadMessageCounts: data };
    case MemberActionTypes.SET_CURRENT_MESSAGE_PAGE:
      return { ...state, currentMessagePage: data };
    // Lock
    case MemberActionTypes.SET_IS_LOCKED_OUT:
      return { ...state, isLockedOut: data };
    case MemberActionTypes.RESET_IS_LOCKED_OUT:
      return { ...state, isLockedOut: false };
    // Quote
    case MemberActionTypes.SET_QUOTE_LOG:
      return { ...state, quote: data };
    case MemberActionTypes.SET_QUOTE_STATUS:
      return { ...state, updatedQuoteStatus: data };
    // Unused action type
    case 'Expected_Arrival':
      return { ...state, expectedArrival: data };
    case 'MPI_TEMPLATE_SUBMITTED':
      return { ...state, isLoading: false, isMpiGeneratorOpen: false };
    case 'ACCOUNT_INFORMATION_UPDATED':
      return { ...state, isLoading: false };
    default:
      return state;
  }
};

export default memberReducer;
